/**
 * `promptForReason(title, label)` — opens a Mantine modal with a single
 * required-text input and resolves to the entered reason on confirm, or
 * `null` if the user cancels.
 *
 * Replaces every `window.prompt(...)` call in the admin views. The
 * native prompt was a Phase-1 placeholder; this gives proper validation
 * (min 3 chars matches the backend Pydantic constraint) and a styled
 * modal that fits the app aesthetic.
 */

import { useState } from "react";
import { Alert, Button, Group, Stack, Text, Textarea } from "@mantine/core";
import { modals } from "@mantine/modals";

interface FormProps {
  label: string;
  defaultValue: string;
  /** Strong red-tinted Alert above the textarea. Use for irreversible /
   *  audit-critical actions (offboarding, key destruction). */
  warning?: string;
  /** Label on the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Minimum trimmed length before confirm is enabled. Defaults to 3. */
  minLength: number;
  /** Plain-text prompt (a name, not an audit reason): hides the
   *  audit-reason helper copy. */
  plain: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- internal to promptForReason
function ReasonForm({
  label,
  defaultValue,
  warning,
  confirmLabel,
  minLength,
  plain,
  onConfirm,
  onCancel,
}: FormProps) {
  const [value, setValue] = useState(defaultValue);
  const trimmed = value.trim();
  const valid = trimmed.length >= minLength;

  return (
    <Stack gap="sm">
      {warning && (
        <Alert color="red" variant="light" title="Heads up">
          {warning}
        </Alert>
      )}
      {!plain && (
        <Text size="sm" c="dimmed">
          Audit-grade reasons must be ≥ 3 characters. Every action you take is
          recorded against this reason in the audit log.
        </Text>
      )}
      <Textarea
        label={label}
        autosize
        minRows={2}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        required
        data-autofocus
      />
      <Group justify="flex-end" gap="xs">
        <Button variant="default" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button
          color={warning ? "red" : "brand"}
          onClick={() => onConfirm(trimmed)}
          disabled={!valid}
          type="button"
        >
          {confirmLabel ?? "Confirm"}
        </Button>
      </Group>
    </Stack>
  );
}

interface PromptForReasonOptions {
  /** Red Alert displayed above the reason field. Use for irreversible /
   *  audit-critical actions so the operator can't miss the consequence. */
  warning?: string;
  /** Label on the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Minimum trimmed length before confirm is enabled. Defaults to 3. */
  minLength?: number;
  /** Plain text prompt (a name, not an audit reason): hides the
   *  audit-reason helper copy. Defaults to false. */
  plain?: boolean;
}

export function promptForReason(
  title: string,
  label = "Reason",
  defaultValueOrOptions: string | PromptForReasonOptions = "",
  maybeOptions?: PromptForReasonOptions,
): Promise<string | null> {
  // Backwards-compatible 3rd-arg overload: a bare string = defaultValue;
  // an object = options (defaultValue stays "").
  let defaultValue = "";
  let options: PromptForReasonOptions | undefined;
  if (typeof defaultValueOrOptions === "string") {
    defaultValue = defaultValueOrOptions;
    options = maybeOptions;
  } else {
    options = defaultValueOrOptions;
  }

  return new Promise((resolve) => {
    const modalId = `reason-${Date.now()}`;
    modals.open({
      modalId,
      title,
      children: (
        <ReasonForm
          label={label}
          defaultValue={defaultValue}
          warning={options?.warning}
          confirmLabel={options?.confirmLabel}
          minLength={options?.minLength ?? 3}
          plain={options?.plain ?? false}
          onConfirm={(reason) => {
            modals.close(modalId);
            resolve(reason);
          }}
          onCancel={() => {
            modals.close(modalId);
            resolve(null);
          }}
        />
      ),
      onClose: () => resolve(null),
    });
  });
}
